import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaClipboardList, FaSignInAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { isChineseLocale } from '../utils/programmeDisplay';
import JupasSurveyCandidateConfirmModal from '../components/widgets/JupasSurveyCandidateConfirmModal';
import {
  hasJupasSurveyCandidateConfirmation,
  setJupasSurveyCandidateConfirmed,
} from '../utils/jupasSurveyCandidateGate';

const SURVEY_SUBMIT_PATH = '/jupas-survey/submit';
const SURVEY_PREFILL_PATH = '/jupas-survey/submit?prefill=account';

function SurveyWriteIcon({ className = 'h-4 w-4 shrink-0' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828a2 2 0 0 0-.586-1.414l-4.828-4.828A2 2 0 0 0 13.172 2H6zm7 1.414L18.586 9H15a1 1 0 0 1-1-1V3.414zM8 12a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1zm0 4a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1z"
      />
      <path d="M17.5 10.5a1.5 1.5 0 0 1 2.12 0l.88.88a1.5 1.5 0 0 1 0 2.12l-5.5 5.5-2.12.3.3-2.12 5.5-5.5z" />
    </svg>
  );
}

export default function JupasSurveyHome() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const introKey = isChineseLocale(i18n.language) ? 'jupasSurvey.introZh' : 'jupasSurvey.introEn';
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState('');

  const accountActionPath = isAuthenticated
    ? SURVEY_PREFILL_PATH
    : `/login?returnTo=${encodeURIComponent(SURVEY_PREFILL_PATH)}`;

  const requestSurveyStart = useCallback((path) => {
    setPendingPath(path);
    setConfirmOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    const path = pendingPath;
    setConfirmOpen(false);
    setPendingPath('');
    setJupasSurveyCandidateConfirmed();
    if (path) navigate(path);
  }, [navigate, pendingPath]);

  const handleExit = useCallback(() => {
    setConfirmOpen(false);
    setPendingPath('');
  }, []);

  return (
    <div className="jupas-survey-home relative overflow-hidden bg-canvas">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-sky-100/85 via-sky-50/35 to-transparent dark:from-sky-950/45 dark:via-sky-950/10 dark:to-transparent"
        aria-hidden
      />
      <div
        className="jupas-survey-home-blob-a pointer-events-none absolute -right-20 top-10 h-64 w-64 rounded-full bg-primary/10 blur-3xl dark:bg-primary/20"
        aria-hidden
      />
      <div
        className="jupas-survey-home-blob-b pointer-events-none absolute -left-20 top-28 h-52 w-52 rounded-full bg-emerald-200/35 blur-3xl dark:bg-emerald-900/20"
        aria-hidden
      />

      <div className="relative mx-auto flex w-full max-w-3xl flex-col px-4 pb-8 pt-2 sm:px-6 sm:pb-10">
        <header className="relative px-2 py-8 text-center sm:py-10">
          <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center">
            <span
              className="jupas-survey-home-icon-glow absolute inset-0 rounded-[1.35rem] bg-primary/15 blur-md"
              aria-hidden
            />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-[1.35rem] border border-primary/20 bg-surface/80 text-primary shadow-sm backdrop-blur-sm">
              <FaClipboardList className="h-7 w-7" aria-hidden />
            </span>
          </div>
          <h1 className="space-y-1">
            <span className="block text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {t('jupasSurvey.home.titleLine1')}
            </span>
            <span className="block text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              {t('jupasSurvey.home.titleLine2')}
            </span>
          </h1>
        </header>

        <article className="rounded-2xl border border-edge/80 bg-surface/90 p-5 shadow-sm backdrop-blur-sm sm:p-6">
          <p className="text-sm leading-7 text-ink sm:text-[15px] sm:leading-8">
            {t(introKey)}
          </p>
        </article>

        <div className="mx-auto mt-7 flex w-full max-w-md flex-col items-stretch gap-2.5">
          <button
            type="button"
            onClick={() => requestSurveyStart(SURVEY_SUBMIT_PATH)}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-base font-bold text-primary-foreground shadow-lg shadow-primary/20 transition hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <SurveyWriteIcon />
            {t('jupasSurvey.home.doSurvey')}
          </button>
          <button
            type="button"
            onClick={() => requestSurveyStart(accountActionPath)}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-edge-strong bg-surface px-6 text-base font-semibold text-ink shadow-sm transition hover:border-primary/30 hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <FaSignInAlt className="h-4 w-4 text-link" aria-hidden />
            {isAuthenticated ? t('jupasSurvey.home.loadMyData') : t('jupasSurvey.home.loginToLoad')}
          </button>
          <p className="pt-1 text-center text-xs leading-relaxed text-ink-subtle">
            {isAuthenticated ? t('jupasSurvey.home.signedInHint') : t('jupasSurvey.home.anonymousHint')}
          </p>
        </div>
      </div>

      <JupasSurveyCandidateConfirmModal
        open={confirmOpen}
        onConfirm={handleConfirm}
        onExit={handleExit}
      />
    </div>
  );
}
